import { StayRoomType, type StayPropertyType } from '../../../__generated__/graphql';
import { PROPERTY_TYPE_LABELS } from '../StayHostPage/stayForm';
import type { IStayQuoteFieldsFragment } from '../../services/stayService/__generated__/queries.generated';

export interface QuoteLine {
  label: string;
  amount: number;
  /** Discounts show as negative amounts in green. */
  discount?: boolean;
}

const money = (n: number) =>
  `$${n.toLocaleString('en-US', { minimumFractionDigits: n % 1 ? 2 : 0, maximumFractionDigits: 2 })}`;

export function formatMoney(n: number): string {
  return money(Math.round(n * 100) / 100);
}

/** The price lines under the booking card, in Airbnb's order (spec 016 W13). */
export function quoteLines(quote: IStayQuoteFieldsFragment): QuoteLine[] {
  const lines: QuoteLine[] = [
    {
      label: `${formatMoney(quote.pricePerNight)} x ${quote.nights} night${quote.nights === 1 ? '' : 's'}`,
      amount: quote.nightlySubtotal,
    },
  ];
  if (quote.weeklyDiscountAmount > 0) {
    lines.push({
      label: 'Weekly stay discount',
      amount: -quote.weeklyDiscountAmount,
      discount: true,
    });
  }
  if (quote.monthlyDiscountAmount > 0) {
    lines.push({
      label: 'Monthly stay discount',
      amount: -quote.monthlyDiscountAmount,
      discount: true,
    });
  }
  if (quote.cleaningFee > 0) lines.push({ label: 'Cleaning fee', amount: quote.cleaningFee });
  if (quote.extraGuestFee > 0)
    lines.push({ label: 'Extra guest fee', amount: quote.extraGuestFee });
  if (quote.petFee > 0) lines.push({ label: 'Pet fee', amount: quote.petFee });
  if (quote.guestServiceFee > 0)
    lines.push({ label: 'Service fee', amount: quote.guestServiceFee });
  if (quote.occupancyTax > 0) lines.push({ label: 'Taxes', amount: quote.occupancyTax });
  return lines;
}

/** Where Reserve goes: checkout with the dates and guests in the URL, so sign-in keeps them (W16). */
export function checkoutPath(
  listingId: string,
  checkIn: string,
  checkOut: string,
  guests: { adults: number; children: number; infants: number; pets: number },
): string {
  const params = new URLSearchParams({ checkIn, checkOut, adults: String(guests.adults) });
  if (guests.children) params.set('children', String(guests.children));
  if (guests.infants) params.set('infants', String(guests.infants));
  if (guests.pets) params.set('pets', String(guests.pets));
  return `/stays/book/${listingId}?${params}`;
}

/** "Entire cabin in Boulder, CO", "Room in a house in Boulder, CO" (Airbnb's headline). */
export function stayHeadline(
  roomType: StayRoomType | null | undefined,
  propertyType: StayPropertyType,
  where: string,
): string {
  const type = (PROPERTY_TYPE_LABELS[propertyType] ?? 'place').toLowerCase();
  const place =
    roomType === StayRoomType.PrivateRoom
      ? `Room in a ${type}`
      : roomType === StayRoomType.SharedRoom
        ? `Shared room in a ${type}`
        : `Entire ${type}`;
  return where ? `${place} in ${where}` : place;
}
